/**
 * Changelog Filters — Client-side multi-select filtering
 * Custom element: <changelog-filters>
 *
 * Filter logic:
 * - OR within a group (checking "feature" + "fix" shows entries with either)
 * - AND between groups (checking "feature" type + "designer" area shows entries matching both)
 */

class ChangelogFilters extends HTMLElement {
  connectedCallback() {
    this.checkboxes = this.querySelectorAll('input[type="checkbox"]');
    this.entries = document.querySelectorAll('.changelog-entry');
    this.statusEl = document.querySelector('[data-status-text]');

    if (!this.entries.length) return;

    this.setStickyOffset();
    this.calculateCounts();
    this.restoreStateFromURL();

    this.checkboxes.forEach(cb => {
      cb.addEventListener('change', () => this.filterEntries());
    });
  }

  setStickyOffset() {
    const header = document.querySelector('.shopify-section-header') || document.querySelector('.section-header');
    if (header) {
      const offset = header.offsetHeight + 20;
      document.documentElement.style.setProperty('--toc-sticky-top', `${offset}px`);
    }

    // Fix: Be Yours sets overflow:hidden on <main> which breaks sticky
    const main = document.querySelector('main.content-for-layout');
    if (main) main.style.setProperty('overflow', 'visible', 'important');
  }

  calculateCounts() {
    const counts = {};
    this.entries.forEach(entry => {
      const tags = (entry.dataset.tags || '').split(',').filter(Boolean);
      tags.forEach(tag => {
        counts[tag] = (counts[tag] || 0) + 1;
      });
    });

    this.querySelectorAll('[data-count-for]').forEach(el => {
      const tag = el.dataset.countFor;
      el.textContent = counts[tag] || 0;
    });
  }

  filterEntries() {
    // Group checked values by group
    const groups = {};
    this.checkboxes.forEach(cb => {
      if (cb.checked) {
        const group = cb.dataset.group || 'other';
        if (!groups[group]) groups[group] = [];
        groups[group].push(cb.value);
      }
    });

    const activeGroups = Object.keys(groups);
    let visibleCount = 0;

    this.entries.forEach(entry => {
      const tags = (entry.dataset.tags || '').split(',').filter(Boolean);

      if (activeGroups.length === 0) {
        // No filters active — show all
        entry.classList.remove('is-hidden');
        visibleCount++;
        return;
      }

      // AND between groups: entry must match at least one tag in EACH active group
      const matchesAll = activeGroups.every(group => {
        return groups[group].some(filterTag => tags.includes(filterTag));
      });

      if (matchesAll) {
        entry.classList.remove('is-hidden');
        visibleCount++;
      } else {
        entry.classList.add('is-hidden');
      }
    });

    this.updateStatus(visibleCount, this.entries.length);
    this.updateURL(groups);
  }

  updateStatus(visible, total) {
    if (!this.statusEl) return;
    const activeCount = Array.from(this.checkboxes).filter(cb => cb.checked).length;
    if (activeCount === 0) {
      this.statusEl.textContent = '\u200B'; // zero-width space (Be Yours hides empty divs)
    } else {
      this.statusEl.textContent = `Showing ${visible} of ${total} updates`;
    }
  }

  updateURL(groups) {
    const activeFilters = [];
    Object.values(groups).forEach(tags => {
      tags.forEach(tag => activeFilters.push(tag));
    });

    const url = new URL(window.location);
    if (activeFilters.length > 0) {
      url.searchParams.set('filters', activeFilters.join(','));
    } else {
      url.searchParams.delete('filters');
    }
    history.replaceState(null, '', url);
  }

  restoreStateFromURL() {
    const url = new URL(window.location);
    const filters = url.searchParams.get('filters');
    if (!filters) return;

    const activeFilters = filters.split(',').filter(Boolean);
    let hasMatch = false;

    this.checkboxes.forEach(cb => {
      if (activeFilters.includes(cb.value)) {
        cb.checked = true;
        hasMatch = true;
      }
    });

    if (hasMatch) {
      this.filterEntries();
    }
  }
}

customElements.define('changelog-filters', ChangelogFilters);
